import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/Layout.jsx';
import {
  Alert, Card, Empty, ErrorState, Input, Loading, NumberInput, Segmented, Select, Stat, Textarea,
} from '../components/ui.jsx';
import { Modal, ConfirmModal } from '../components/Modal.jsx';
import { OpeningBalanceForm } from '../components/OpeningBalanceForm.jsx';
import { EntityPicker } from '../components/EntityPicker.jsx';
import { IconExchange, IconPlus } from '../components/Icons.jsx';
import { api } from '../lib/api.js';
import { useApi, useDebounced } from '../lib/hooks.js';
import { useAuth } from '../lib/auth.jsx';
import { useToast } from '../lib/toast.jsx';
import { t } from '../lib/i18n.js';
import { formatDate, money, moneyPair, num, today } from '../lib/format.js';

const KIND_LABEL = {
  deposit: t.exchange.deposit,
  withdrawal: t.exchange.withdrawal,
  transfer: t.exchange.transfer,
  buy_usd: t.exchange.buy_usd,
  sell_usd: t.exchange.sell_usd,
};
const KIND_BADGE = { deposit: 'cash', withdrawal: 'debt', transfer: 'debt', buy_usd: 'buy', sell_usd: 'sell' };

export default function Exchange() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search);
  const [editingCompany, setEditingCompany] = useState(null);
  const [deletingCompany, setDeletingCompany] = useState(null);
  const [newTx, setNewTx] = useState(null); // { company } or null
  const [editingTx, setEditingTx] = useState(null);
  const [deletingTx, setDeletingTx] = useState(null);
  const [settingOpening, setSettingOpening] = useState(null);
  const [busy, setBusy] = useState(false);

  const [txCompany, setTxCompany] = useState('');
  const [txKind, setTxKind] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const companies = useApi('/api/exchange/companies', { q: debouncedSearch, include_inactive: true });
  // Unfiltered, so a search query narrowing the card list below does not also
  // hide companies from the transaction form's picker or from the totals.
  const allCompanies = useApi('/api/exchange/companies', { include_inactive: true });
  const txList = useApi('/api/exchange/transactions', {
    company_id: txCompany, kind: txKind, from, to, limit: 200,
  });

  const totals = (allCompanies.data || []).reduce(
    (acc, c) => ({
      held_iqd: acc.held_iqd + Math.max(0, Number(c.balance_iqd)),
      held_usd: acc.held_usd + Math.max(0, Number(c.balance_usd)),
      owed_iqd: acc.owed_iqd + Math.max(0, -Number(c.balance_iqd)),
      owed_usd: acc.owed_usd + Math.max(0, -Number(c.balance_usd)),
    }),
    { held_iqd: 0, held_usd: 0, owed_iqd: 0, owed_usd: 0 },
  );

  const reloadAll = () => {
    companies.reload();
    allCompanies.reload();
    txList.reload();
  };

  const removeTx = async () => {
    setBusy(true);
    try {
      await api.del(`/api/exchange/transactions/${deletingTx.id}`);
      toast.success(t.exchange.transactionDeleted);
      setDeletingTx(null);
      reloadAll();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const removeCompany = async () => {
    setBusy(true);
    try {
      await api.del(`/api/exchange/companies/${deletingCompany.id}`);
      toast.success(t.exchange.companyDeleted);
      setDeletingCompany(null);
      reloadAll();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title={t.exchange.title}
        subtitle={t.exchange.subtitle}
        actions={
          <div className="btn-row no-print">
            <button type="button" className="btn btn-sm" onClick={() => setEditingCompany({})}>
              <IconPlus /> {t.exchange.addCompany}
            </button>
            <button type="button" className="btn btn-sm btn-primary" onClick={() => setNewTx({})}>
              <IconExchange /> {t.exchange.newTransaction}
            </button>
          </div>
        }
      />

      <div className="page">
        <div className="stat-grid">
          <Stat label={t.exchange.totalHeld} value={money(totals.held_iqd, 'IQD')} hint={money(totals.held_usd, 'USD')} />
          <Stat
            label={t.exchange.totalOwed}
            value={money(totals.owed_iqd, 'IQD')}
            hint={money(totals.owed_usd, 'USD')}
            tone={totals.owed_iqd > 0 || totals.owed_usd > 0 ? 'danger' : ''}
          />
        </div>

        <Card title={t.exchange.companies}>
          <Input
            label={t.common.search}
            placeholder={t.exchange.searchHint}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <p className="small muted mt-0">{t.exchange.accountNote}</p>

          <ErrorState error={companies.error} onRetry={companies.reload} />
          {companies.loading && !companies.data && <Loading />}

          {companies.data && companies.data.length === 0 && (
            <Empty
              title={t.exchange.emptyTitle}
              message={t.exchange.emptyHint}
              action={
                <button type="button" className="btn btn-primary" onClick={() => setEditingCompany({})}>
                  <IconPlus /> {t.exchange.addCompany}
                </button>
              }
            />
          )}

          {companies.data && companies.data.length > 0 && (
            <div className="record-list">
              {companies.data.map((co) => {
                const owed = Number(co.balance_iqd) < 0 || Number(co.balance_usd) < 0;
                return (
                  <div key={co.id} className="record">
                    <div className="record-top">
                      <Link className="title" to={`/exchange/${co.id}`}>
                        {co.name}
                      </Link>
                      <span className={`amount ${owed ? 'neg' : 'pos'}`}>{money(co.balance_iqd, 'IQD')}</span>
                    </div>
                    <div className="record-meta">
                      {co.phone && <span>{co.phone}</span>}
                      {Number(co.balance_usd) !== 0 && (
                        <span className={Number(co.balance_usd) < 0 ? 'neg' : 'pos'}>{money(co.balance_usd, 'USD')}</span>
                      )}
                      <span className="badge muted">{owed ? t.exchange.weOwe : t.exchange.heldByThem}</span>
                      {!co.active && <span className="badge muted">{t.common.inactive}</span>}
                    </div>
                    <div className="btn-row no-print" style={{ marginTop: 4 }}>
                      <button type="button" className="btn btn-sm" onClick={() => setNewTx({ company: co })}>
                        <IconExchange /> {t.exchange.newTransaction}
                      </button>
                      <button type="button" className="btn btn-sm" onClick={() => navigate(`/exchange/${co.id}`)}>
                        {t.exchange.statement}
                      </button>
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditingCompany(co)}>
                        {t.common.edit}
                      </button>
                      {isAdmin && (
                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setSettingOpening(co)}>
                          {t.openingBalance.action}
                        </button>
                      )}
                      {isAdmin && (
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => setDeletingCompany(co)}>
                          {t.common.delete}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card title={t.exchange.recentTransactions}>
          <div className="filters">
            <Select
              label={t.exchange.company}
              placeholder={t.exchange.allCompanies}
              value={txCompany}
              onChange={(e) => setTxCompany(e.target.value)}
              options={(allCompanies.data || []).map((c) => ({ value: c.id, label: c.name }))}
            />
            <Select
              label={t.exchange.kind}
              placeholder={t.exchange.allKinds}
              value={txKind}
              onChange={(e) => setTxKind(e.target.value)}
              options={Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label }))}
            />
            <Input label={t.common.from} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <Input label={t.common.to} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>

          {txList.data?.totals && (
            <div className="stat-grid">
              <Stat label={t.exchange.entryCount} value={num(txList.data.totals.count)} />
              <Stat
                label={t.exchange.totalCommission}
                value={moneyPair(txList.data.totals.commission_iqd, txList.data.totals.commission_usd, { zero: '-' })}
              />
              <Stat
                label={t.exchange.totalDeposits}
                value={moneyPair(txList.data.totals.deposits_iqd, txList.data.totals.deposits_usd, { zero: '-' })}
              />
              <Stat
                label={t.exchange.totalTransfers}
                value={moneyPair(txList.data.totals.transfers_iqd, txList.data.totals.transfers_usd, { zero: '-' })}
              />
            </div>
          )}

          <ErrorState error={txList.error} onRetry={txList.reload} />
          {txList.loading && !txList.data && <Loading />}
          {txList.data && txList.data.rows.length === 0 && <Empty title={t.exchange.noTransactions} />}

          {txList.data && txList.data.rows.length > 0 && (
            <div className="record-list">
              {txList.data.rows.map((row) => (
                <div key={row.id} className="record">
                  <div className="record-top">
                    <span className={`badge ${KIND_BADGE[row.kind]}`}>{KIND_LABEL[row.kind]}</span>
                    <span className="title">{row.company_name}</span>
                    <span className="amount">
                      {row.kind === 'buy_usd' || row.kind === 'sell_usd'
                        ? moneyPair(row.iqd_amount, row.usd_amount)
                        : money(row.amount, row.currency)}
                    </span>
                  </div>
                  <div className="record-meta">
                    <span>{formatDate(row.happened_on)}</span>
                    {row.customer_name && <span>{row.customer_name}</span>}
                    {row.supplier_name && <span>{row.supplier_name}</span>}
                    {!row.supplier_name && row.beneficiary && <span>{row.beneficiary}</span>}
                    {row.reference && <span>{row.reference}</span>}
                    {Number(row.commission) > 0 && (
                      <span className="muted">{t.exchange.commission}: {money(row.commission, row.commission_currency)}</span>
                    )}
                  </div>
                  <div className="btn-row no-print" style={{ marginTop: 4 }}>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditingTx(row)}>
                      {t.common.edit}
                    </button>
                    {isAdmin && (
                      <button type="button" className="btn btn-sm btn-danger" onClick={() => setDeletingTx(row)}>
                        {t.common.delete}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {editingCompany && (
        <CompanyForm
          company={editingCompany}
          onClose={() => setEditingCompany(null)}
          onSaved={(saved) => {
            setEditingCompany(null);
            toast.success(t.exchange.companySaved(saved.name));
            companies.reload();
          }}
        />
      )}

      {newTx && (
        <TransactionForm
          companies={allCompanies.data || []}
          initialCompanyId={newTx.company?.id}
          onClose={() => setNewTx(null)}
          onSaved={() => {
            setNewTx(null);
            toast.success(t.exchange.transactionSaved);
            reloadAll();
          }}
        />
      )}

      {editingTx && (
        <TransactionForm
          companies={allCompanies.data || []}
          transaction={editingTx}
          onClose={() => setEditingTx(null)}
          onSaved={() => {
            setEditingTx(null);
            toast.success(t.exchange.transactionSaved);
            reloadAll();
          }}
        />
      )}

      {deletingTx && (
        <ConfirmModal
          title={t.exchange.deleteTransaction}
          message={t.exchange.deleteTransactionMessage}
          onConfirm={removeTx}
          onClose={() => setDeletingTx(null)}
          busy={busy}
        />
      )}

      {deletingCompany && (
        <ConfirmModal
          title={t.exchange.deleteCompany}
          message={t.exchange.deleteCompanyMessage(deletingCompany.name)}
          onConfirm={removeCompany}
          onClose={() => setDeletingCompany(null)}
          busy={busy}
        />
      )}

      {settingOpening && (
        <OpeningBalanceForm
          partyType="exchange"
          partyId={settingOpening.id}
          partyName={settingOpening.name}
          onClose={() => setSettingOpening(null)}
          onSaved={() => {
            setSettingOpening(null);
            reloadAll();
          }}
        />
      )}
    </>
  );
}

function CompanyForm({ company, onClose, onSaved }) {
  const isEdit = Boolean(company.id);
  const [form, setForm] = useState({
    name: company.name || '',
    phone: company.phone || '',
    address: company.address || '',
    notes: company.notes || '',
    active: company.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = isEdit
        ? await api.patch(`/api/exchange/companies/${company.id}`, form)
        : await api.post('/api/exchange/companies', form);
      onSaved(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={isEdit ? t.exchange.editCompany : t.exchange.newCompany}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            {t.common.cancel}
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={busy || !form.name.trim()}>
            {busy ? <span className="spinner" /> : t.common.save}
          </button>
        </>
      }
    >
      {error && <Alert tone="error">{error}</Alert>}
      <Input label={t.common.name} required value={form.name} onChange={(e) => set({ name: e.target.value })} />
      <Input
        label={t.common.phone}
        value={form.phone}
        onChange={(e) => set({ phone: e.target.value })}
        inputMode="tel"
        dir="ltr"
      />
      <Input label={t.common.address} value={form.address} onChange={(e) => set({ address: e.target.value })} />
      <Textarea label={t.common.notes} value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={2} />
      {isEdit && (
        <Select
          label={t.common.status}
          value={form.active ? 'true' : 'false'}
          onChange={(e) => set({ active: e.target.value === 'true' })}
          options={[
            { value: 'true', label: t.common.active },
            { value: 'false', label: t.common.inactive },
          ]}
        />
      )}
    </Modal>
  );
}

/**
 * One form for all five transaction kinds. The kind picks which fields show;
 * it is fixed once a transaction exists, matching the server, which ignores
 * any attempt to change it on an edit.
 */
export function TransactionForm({ companies, transaction, initialCompanyId, onClose, onSaved }) {
  const isEdit = Boolean(transaction);
  const kind = transaction?.kind;

  const initialTransferDest = transaction?.supplier_id
    ? 'supplier'
    : transaction?.shareholder_id
    ? 'shareholder'
    : transaction?.payroll_staff_id
    ? 'staff'
    : 'beneficiary';

  const [form, setForm] = useState({
    company_id: transaction?.company_id ?? initialCompanyId ?? '',
    kind: kind || 'deposit',
    happened_on: transaction?.happened_on || today(),
    reference: transaction?.reference || '',
    currency: transaction?.currency || 'IQD',
    amount: transaction?.amount || '',
    commission: Number(transaction?.commission) ? transaction.commission : '',
    commission_currency: transaction?.commission_currency || 'IQD',
    transferDest: initialTransferDest,
    beneficiary: transaction?.beneficiary || '',
    supplier_id: transaction?.supplier_id || null,
    supplier_name: transaction?.supplier_name || '',
    staff_id: transaction?.payroll_staff_id || '',
    period_month: transaction?.payroll_period_month?.slice(0, 7) || '',
    depositLink: transaction?.customer_id ? 'customer' : transaction?.shareholder_id ? 'shareholder' : 'none',
    customer_id: transaction?.customer_id || null,
    customer_name: transaction?.customer_name || '',
    // A withdrawal's supplier link is optional (unlike a transfer's
    // destination) - plain cash withdrawals stay valid, matching today.
    withdrawalLink: transaction?.supplier_id ? 'supplier' : 'none',
    shareholder_id: transaction?.shareholder_id || null,
    shareholder_name: transaction?.shareholder_name || '',
    rate: transaction?.rate || '',
    usd_amount: transaction?.usd_amount || '',
    notes: transaction?.notes || '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const customers = useApi(form.kind === 'deposit' ? '/api/customers' : null, null, { enabled: form.kind === 'deposit' });
  const suppliers = useApi(
    form.kind === 'transfer' || form.kind === 'withdrawal' ? '/api/suppliers' : null,
    null,
    { enabled: form.kind === 'transfer' || form.kind === 'withdrawal' },
  );
  const staffList = useApi(form.kind === 'transfer' ? '/api/staff' : null, null, { enabled: form.kind === 'transfer' });
  const shareholders = useApi(
    form.kind === 'deposit' || form.kind === 'transfer' ? '/api/shareholders' : null,
    null,
    { enabled: form.kind === 'deposit' || form.kind === 'transfer' },
  );

  const effectiveKind = isEdit ? kind : form.kind;
  const isCash = ['deposit', 'withdrawal', 'transfer'].includes(effectiveKind);
  const isRate = ['buy_usd', 'sell_usd'].includes(effectiveKind);
  const iqdPreview =
    isRate && Number(form.rate) > 0 && Number(form.usd_amount) > 0
      ? (Number(form.rate) * Number(form.usd_amount)).toFixed(2)
      : null;

  const transferNeedsDestination =
    !isEdit &&
    effectiveKind === 'transfer' &&
    ((form.transferDest === 'beneficiary' && !form.beneficiary.trim()) ||
      (form.transferDest === 'supplier' && !form.supplier_id && !form.supplier_name.trim()) ||
      (form.transferDest === 'shareholder' && !form.shareholder_id && !form.shareholder_name.trim()) ||
      (form.transferDest === 'staff' && (!form.staff_id || !form.period_month)));

  const canSave =
    form.company_id &&
    form.happened_on &&
    (isCash ? Number(form.amount) > 0 && form.currency : true) &&
    (isRate ? Number(form.rate) > 0 && Number(form.usd_amount) > 0 : true) &&
    !transferNeedsDestination;

  const save = async () => {
    setBusy(true);
    setError(null);
    const payload = {
      company_id: Number(form.company_id),
      happened_on: form.happened_on,
      reference: form.reference,
      notes: form.notes,
      commission: form.commission || '0',
      commission_currency: form.commission_currency,
    };
    if (!isEdit) payload.kind = form.kind;
    if (isCash) {
      payload.currency = form.currency;
      payload.amount = form.amount;
    }
    if (effectiveKind === 'transfer' && !isEdit) {
      payload.beneficiary = form.transferDest === 'beneficiary' ? form.beneficiary : '';
      payload.supplier_id = form.transferDest === 'supplier' ? form.supplier_id : '';
      payload.supplier_name = form.transferDest === 'supplier' && !form.supplier_id ? form.supplier_name : undefined;
      payload.shareholder_id = form.transferDest === 'shareholder' ? form.shareholder_id : '';
      payload.shareholder_name =
        form.transferDest === 'shareholder' && !form.shareholder_id ? form.shareholder_name : undefined;
      if (form.transferDest === 'staff') {
        payload.staff_id = Number(form.staff_id);
        payload.period_month = `${form.period_month}-01`;
      }
    }
    if (effectiveKind === 'withdrawal' && !isEdit) {
      payload.supplier_id = form.withdrawalLink === 'supplier' ? form.supplier_id : '';
      payload.supplier_name =
        form.withdrawalLink === 'supplier' && !form.supplier_id ? form.supplier_name : undefined;
    }
    if (effectiveKind === 'deposit' && !isEdit) {
      payload.customer_id = form.depositLink === 'customer' ? form.customer_id : '';
      payload.customer_name = form.depositLink === 'customer' && !form.customer_id ? form.customer_name : undefined;
      payload.shareholder_id = form.depositLink === 'shareholder' ? form.shareholder_id : '';
      payload.shareholder_name =
        form.depositLink === 'shareholder' && !form.shareholder_id ? form.shareholder_name : undefined;
    }
    if (isRate) {
      payload.rate = form.rate;
      payload.usd_amount = form.usd_amount;
    }

    try {
      if (isEdit) await api.patch(`/api/exchange/transactions/${transaction.id}`, payload);
      else {
        await api.post('/api/exchange/transactions', payload, {
          queueOffline: true,
          label: `${t.exchange.title} — ${KIND_LABEL[form.kind]}`,
        });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const selectedCustomer = (customers.data || []).find((c) => c.id === form.customer_id);
  const selectedSupplier = (suppliers.data || []).find((s) => s.id === form.supplier_id);
  const selectedShareholder = (shareholders.data || []).find((s) => s.id === form.shareholder_id);

  const transferDestinationLabel = () => {
    if (transaction?.supplier_name) return t.exchange.supplierPaidNote(transaction.supplier_name);
    if (transaction?.shareholder_name) return t.exchange.shareholderPaidNote(transaction.shareholder_name);
    if (transaction?.payroll_staff_name) return t.exchange.payrollPaidNote(transaction.payroll_staff_name);
    return transaction?.beneficiary || t.exchange.noDestination;
  };

  return (
    <Modal
      title={isEdit ? t.exchange.editTransaction : t.exchange.newTransaction}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            {t.common.cancel}
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={busy || !canSave}>
            {busy ? <span className="spinner" /> : t.common.save}
          </button>
        </>
      }
    >
      {error && <Alert tone="error">{error}</Alert>}

      <Select
        label={t.exchange.company}
        required
        placeholder={t.exchange.allCompanies}
        value={form.company_id}
        onChange={(e) => set({ company_id: e.target.value })}
        options={companies.map((c) => ({ value: c.id, label: c.name }))}
        disabled={isEdit}
      />

      {isEdit ? (
        <Alert tone="info">
          {KIND_LABEL[kind]} — {t.exchange.kindLocked}
        </Alert>
      ) : (
        <Segmented
          label={t.exchange.kind}
          value={form.kind}
          onChange={(v) => set({ kind: v })}
          options={Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label }))}
          hint={{
            deposit: t.exchange.depositHint,
            withdrawal: t.exchange.withdrawalHint,
            transfer: t.exchange.transferHint,
            buy_usd: t.exchange.buyUsdHint,
            sell_usd: t.exchange.sellUsdHint,
          }[form.kind]}
        />
      )}

      <Input
        label={t.common.date}
        required
        type="date"
        value={form.happened_on}
        onChange={(e) => set({ happened_on: e.target.value })}
      />

      {isCash && (
        <div className="form-grid cols-2">
          <NumberInput label={t.exchange.amount} required value={form.amount} onChange={(amount) => set({ amount })} />
          <Segmented
            label={t.exchange.currency}
            value={form.currency}
            onChange={(currency) => set({ currency })}
            options={[
              { value: 'IQD', label: t.report.iqd },
              { value: 'USD', label: t.report.usd },
            ]}
          />
        </div>
      )}

      {isRate && (
        <>
          <div className="form-grid cols-2">
            <NumberInput label={t.exchange.rate} required value={form.rate} onChange={(rate) => set({ rate })} hint={t.exchange.rateHint} />
            <NumberInput
              label={t.exchange.usdAmount}
              required
              value={form.usd_amount}
              onChange={(usd_amount) => set({ usd_amount })}
            />
          </div>
          <Input label={t.exchange.iqdAmount} value={iqdPreview ? money(iqdPreview, 'IQD') : ''} readOnly hint={t.exchange.iqdAmountComputed} />
        </>
      )}

      {effectiveKind === 'transfer' && (
        <>
          {isEdit ? (
            <Alert tone="info">{transferDestinationLabel()}</Alert>
          ) : (
            <>
              <Segmented
                label={t.exchange.transferDestination}
                value={form.transferDest}
                onChange={(v) => set({ transferDest: v })}
                options={[
                  { value: 'beneficiary', label: t.exchange.beneficiary },
                  { value: 'supplier', label: t.purchase.supplier },
                  { value: 'shareholder', label: t.shareholder.title },
                  { value: 'staff', label: t.exchange.payrollDestination },
                ]}
              />

              {form.transferDest === 'beneficiary' && (
                <Input
                  label={t.exchange.beneficiary}
                  value={form.beneficiary}
                  onChange={(e) => set({ beneficiary: e.target.value })}
                  hint={t.exchange.beneficiaryHint}
                />
              )}

              {form.transferDest === 'supplier' && (
                <>
                  <EntityPicker
                    label={t.purchase.supplier}
                    items={suppliers.data || []}
                    value={form.supplier_id}
                    name={form.supplier_name}
                    onChange={({ id, name }) => set({ supplier_id: id, supplier_name: name })}
                    createLabel={t.party.suppliers.addOne}
                  />
                  {(selectedSupplier || form.supplier_name) && (
                    <Alert tone="info">{t.exchange.supplierPaidNote(selectedSupplier?.name || form.supplier_name)}</Alert>
                  )}
                </>
              )}

              {form.transferDest === 'shareholder' && (
                <>
                  <EntityPicker
                    label={t.shareholder.title}
                    items={shareholders.data || []}
                    value={form.shareholder_id}
                    name={form.shareholder_name}
                    onChange={({ id, name }) => set({ shareholder_id: id, shareholder_name: name })}
                    createLabel={t.shareholder.addOne}
                  />
                  {(selectedShareholder || form.shareholder_name) && (
                    <Alert tone="info">{t.exchange.shareholderPaidNote(selectedShareholder?.name || form.shareholder_name)}</Alert>
                  )}
                </>
              )}

              {form.transferDest === 'staff' && (
                <>
                  <Select
                    label={t.exchange.payrollDestination}
                    placeholder={t.exchange.payrollDestination}
                    value={form.staff_id}
                    onChange={(e) => set({ staff_id: e.target.value })}
                    options={(staffList.data || []).map((s) => ({ value: s.id, label: s.name }))}
                  />
                  <Input
                    label={t.exchange.payrollMonth}
                    type="month"
                    value={form.period_month}
                    onChange={(e) => set({ period_month: e.target.value })}
                  />
                  {form.staff_id && (
                    <Alert tone="info">
                      {t.exchange.payrollPaidNote((staffList.data || []).find((s) => String(s.id) === String(form.staff_id))?.name || '')}
                    </Alert>
                  )}
                </>
              )}

              {transferNeedsDestination && <Alert tone="error">{t.exchange.noDestination}</Alert>}
            </>
          )}
        </>
      )}

      {effectiveKind === 'withdrawal' && (
        <>
          {isEdit ? (
            transaction?.supplier_name && (
              <Alert tone="info">{t.exchange.withdrawalSupplierPaidNote(transaction.supplier_name)}</Alert>
            )
          ) : (
            <>
              <Segmented
                label={t.exchange.withdrawalLink}
                value={form.withdrawalLink}
                onChange={(v) => set({ withdrawalLink: v })}
                options={[
                  { value: 'none', label: t.exchange.depositLinkNone },
                  { value: 'supplier', label: t.purchase.supplier },
                ]}
                hint={t.exchange.withdrawalLinkHint}
              />
              {form.withdrawalLink === 'supplier' && (
                <>
                  <EntityPicker
                    label={t.purchase.supplier}
                    items={suppliers.data || []}
                    value={form.supplier_id}
                    name={form.supplier_name}
                    onChange={({ id, name }) => set({ supplier_id: id, supplier_name: name })}
                    createLabel={t.party.suppliers.addOne}
                  />
                  {(selectedSupplier || form.supplier_name) && (
                    <Alert tone="info">
                      {t.exchange.withdrawalSupplierPaidNote(selectedSupplier?.name || form.supplier_name)}
                    </Alert>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      {effectiveKind === 'deposit' && (
        <>
          {isEdit ? (
            transaction?.customer_name || transaction?.shareholder_name ? (
              <Alert tone="info">
                {transaction?.customer_name
                  ? t.exchange.customerPaidNote(transaction.customer_name)
                  : t.exchange.shareholderDepositNote(transaction.shareholder_name)}
              </Alert>
            ) : null
          ) : (
            <>
              <Segmented
                label={t.exchange.depositLink}
                value={form.depositLink}
                onChange={(v) => set({ depositLink: v })}
                options={[
                  { value: 'none', label: t.exchange.depositLinkNone },
                  { value: 'customer', label: t.sale.customer },
                  { value: 'shareholder', label: t.shareholder.title },
                ]}
                hint={t.exchange.collectedFromCustomerHint}
              />
              {form.depositLink === 'customer' && (
                <>
                  <EntityPicker
                    label={t.sale.customer}
                    items={customers.data || []}
                    value={form.customer_id}
                    name={form.customer_name}
                    onChange={({ id, name }) => set({ customer_id: id, customer_name: name })}
                    createLabel={t.party.customers.addOne}
                  />
                  {(selectedCustomer || form.customer_name) && (
                    <Alert tone="info">{t.exchange.customerPaidNote(selectedCustomer?.name || form.customer_name)}</Alert>
                  )}
                </>
              )}
              {form.depositLink === 'shareholder' && (
                <>
                  <EntityPicker
                    label={t.shareholder.title}
                    items={shareholders.data || []}
                    value={form.shareholder_id}
                    name={form.shareholder_name}
                    onChange={({ id, name }) => set({ shareholder_id: id, shareholder_name: name })}
                    createLabel={t.shareholder.addOne}
                  />
                  {(selectedShareholder || form.shareholder_name) && (
                    <Alert tone="info">
                      {t.exchange.shareholderDepositNote(selectedShareholder?.name || form.shareholder_name)}
                    </Alert>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      <div className="form-grid cols-2">
        <NumberInput
          label={t.exchange.commission}
          value={form.commission}
          onChange={(commission) => set({ commission })}
          hint={t.exchange.commissionHint}
        />
        <Segmented
          label={t.exchange.commissionCurrency}
          value={form.commission_currency}
          onChange={(commission_currency) => set({ commission_currency })}
          options={[
            { value: 'IQD', label: t.report.iqd },
            { value: 'USD', label: t.report.usd },
          ]}
        />
      </div>

      <Input label={t.exchange.reference} value={form.reference} onChange={(e) => set({ reference: e.target.value })} hint={t.exchange.referenceHint} />
      <Textarea label={t.common.notes} value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={2} />
    </Modal>
  );
}
